// src/app/components/DeviceTypesOverviewCard.tsx
import React, { useMemo } from "react";
import {
  Card,
  Stack,
  Text,
  Group,
  Badge,
  Button,
  Table,
  Alert,
  Code,
  Tooltip,
  Box,
} from "@mantine/core";
import { FaSyncAlt } from "react-icons/fa";
import { openapi } from "../../openapi";
import { useProjectId } from "../hooks/useProjectId";
import { DEVICES_TYPES_SETTINGS } from "@multiconsult-group/multiplacer-core";
import { IDeviceCategoryEnum } from "@backend/src/shared/schemas/foundationSchema";

export const DeviceTypesOverviewCard: React.FC = () => {
  const projectNo = useProjectId();

  const {
    data: stats,
    isLoading,
    isError,
    error,
    refetch,
  } = openapi.endpoints.getMultiplacerDevicetypesStats.useQuery({
    projectNo,
  });

  const [
    aggregate,
    { isLoading: isAggregating, isError: isAggError, error: aggError, isSuccess: isAggSuccess },
  ] = openapi.endpoints.postMultiplacerDevicetypesAggregate.useMutation();

  const handleAggregate = async () => {
    await aggregate({
      body: { projectNo },
    }).unwrap();
    await refetch();
  };

  const deviceCategoryKeys = Object.keys(DEVICES_TYPES_SETTINGS) as IDeviceCategoryEnum[];

  const categoryRows = useMemo(() => {
    if (!stats) {
      return deviceCategoryKeys.map((cat) => ({
        cat,
        count: 0,
        color: DEVICES_TYPES_SETTINGS[cat]?.color ?? "#cccccc",
      }));
    }

    const counts = new Map<IDeviceCategoryEnum, number>();

    stats.categories.forEach((c: { deviceCategory: IDeviceCategoryEnum; count: number }) => {
      if (deviceCategoryKeys.includes(c.deviceCategory)) {
        counts.set(c.deviceCategory, c.count);
      }
    });

    return deviceCategoryKeys.map((cat) => ({
      cat,
      count: counts.get(cat) ?? 0,
      color: DEVICES_TYPES_SETTINGS[cat]?.color ?? "#cccccc",
    }));
  }, [stats, deviceCategoryKeys]);

  return (
    <Card withBorder radius="lg" p="md">
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start">
          <Stack gap={2}>
            <Text fw={600}>Device-typer fra IFC</Text>
            <Text size="sm" c="dimmed">
              Oversikt over unike device-typer fra IFC for dette prosjektet (
              <Code>{projectNo}</Code>). Fargene er de samme som brukes i visningen.
            </Text>
          </Stack>
          <Tooltip label="Les inn device-typer på nytt fra IFC (erstatter eksisterende liste)">
            <Button
              variant="light"
              size="xs"
              leftSection={<FaSyncAlt size={12} />}
              loading={isAggregating}
              onClick={handleAggregate}
            >
              Regenerer device-typer
            </Button>
          </Tooltip>
        </Group>

        <Text size="xs" c="dimmed">
          • DeviceCategory settes automatisk ut fra IFC-klassen (lys, sprinkler, sensor osv.) <br />
          • For luftterminaler (IfcAirTerminal) brukes prosjektets{" "}
          <Code>ventilation.compCodeDirectionRules</Code> til å skille mellom tilluft og
          avtrekk/retur.
        </Text>

        {isError && (
          <Alert color="red" variant="light" title="Kunne ikke hente statistikk">
            {(error as any)?.data?.message ||
              (error as any)?.message ||
              "Det oppstod en feil ved henting av device-typer."}
          </Alert>
        )}

        {isAggError && (
          <Alert color="red" variant="light" title="Aggregering feilet">
            {(aggError as any)?.data?.message ||
              (aggError as any)?.message ||
              "Det oppstod en feil under regenerering av device-typer."}
          </Alert>
        )}

        {isAggSuccess && (
          <Alert color="green" variant="light" title="Aggregering fullført">
            Device-typer er oppdatert fra IFC for prosjekt <Code>{projectNo}</Code>.
          </Alert>
        )}

        {isLoading ? (
          <Text size="sm" c="dimmed">
            Laster statistikk…
          </Text>
        ) : stats ? (
          <>
            <Group gap="xs">
              <Badge variant="light" color="blue">
                Totalt {stats.total} device-typer
              </Badge>
            </Group>

            <Table striped withTableBorder withColumnBorders highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Kategori</Table.Th>
                  <Table.Th style={{ width: 120, textAlign: "right" }}>Antall typer</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {categoryRows.map(({ cat, count, color }) => (
                  <Table.Tr key={cat}>
                    <Table.Td>
                      <Group gap={8} align="center">
                        <Box
                          w={18}
                          h={18}
                          style={{
                            borderRadius: 4,
                            border: "1px solid rgba(0,0,0,0.2)",
                            backgroundColor: color,
                          }}
                        />
                        <Badge variant="outline">{cat}</Badge>
                        <Text size="sm">
                          {DEVICES_TYPES_SETTINGS[cat as IDeviceCategoryEnum].label}
                        </Text>
                      </Group>
                    </Table.Td>
                    <Table.Td style={{ textAlign: "right" }}>
                      <Text size="sm" fw={500}>
                        {count}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </>
        ) : (
          <Text size="sm" c="dimmed">
            Ingen device-typer funnet for dette prosjektet ennå. Prøv å{" "}
            <b>regenerere device-typer</b> fra IFC.
          </Text>
        )}
      </Stack>
    </Card>
  );
};

export default DeviceTypesOverviewCard;
